import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { approachSteps } from "../data/mock";
import { ArrowRight } from "lucide-react";

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.15, ...options }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
};

export default function ApproachPage() {
  const [heroRef, heroInView] = useInView();
  const [stepsRef, stepsInView] = useInView();
  const [valuesRef, valuesInView] = useInView();

  const values = [
    {
      title: "Design First",
      description: "We start with design, not a price list. Your home should inspire you every single day.",
    },
    {
      title: "Collaborative Spirit",
      description: "You're not handing off your dream — you're part of the creative process from day one.",
    },
    {
      title: "Intentional Details",
      description: "Every material, every angle, every line of light is chosen with purpose and care.",
    },
    {
      title: "Transparent Partnership",
      description: "No surprises, no hidden costs. Just honest communication and mutual respect throughout.",
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section
        ref={heroRef}
        className="pt-32 md:pt-44 pb-20 md:pb-28 px-6 md:px-12 bg-[#FAFAF7]"
      >
        <div className="max-w-[1400px] mx-auto">
          <span
            className={`font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block transition-all duration-1000 ${
              heroInView ? "opacity-100" : "opacity-0"
            }`}
          >
            Our Approach
          </span>
          <h1
            className={`font-display text-5xl md:text-6xl lg:text-7xl text-stone-900 leading-[1.05] mb-8 max-w-3xl transition-all duration-1000 delay-200 ${
              heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            A home should be designed around the life you want to live.
          </h1>
          <p
            className={`font-body text-stone-500 text-lg leading-relaxed max-w-2xl transition-all duration-1000 delay-400 ${
              heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            We're not a production builder. We're a design studio that happens to build
            extraordinary homes. Every project starts with listening, understanding, and
            imagining what's possible.
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section ref={stepsRef} className="py-28 md:py-40 px-6 md:px-12 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div
            className={`mb-20 transition-all duration-1000 ${
              stepsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
            }`}
          >
            <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
              The Process
            </span>
            <h2 className="font-display text-4xl md:text-5xl text-stone-900 leading-[1.1]">
              Four phases, one vision
            </h2>
          </div>

          <div className="space-y-0">
            {approachSteps.map((step, i) => (
              <div
                key={step.number}
                className={`grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 py-12 md:py-16 border-t border-stone-200 transition-all duration-1000 ${
                  stepsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                }`}
                style={{ transitionDelay: `${i * 200}ms` }}
              >
                <div className="md:col-span-2">
                  <span className="font-display text-6xl md:text-7xl text-stone-200">
                    {step.number}
                  </span>
                </div>
                <div className="md:col-span-3">
                  <h3 className="font-display text-2xl md:text-3xl text-stone-900">
                    {step.title}
                  </h3>
                </div>
                <div className="md:col-span-7">
                  <p className="font-body text-stone-500 text-base md:text-lg leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section ref={valuesRef} className="py-28 md:py-40 px-6 md:px-12 bg-stone-950">
        <div className="max-w-[1400px] mx-auto">
          <div
            className={`mb-16 md:mb-20 transition-all duration-1000 ${
              valuesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
            }`}
          >
            <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
              What We Believe
            </span>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.1]">
              Our design values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            {values.map((value, i) => (
              <div
                key={value.title}
                className={`transition-all duration-1000 ${
                  valuesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                }`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <h3 className="font-display text-xl md:text-2xl text-white mb-3">
                  {value.title}
                </h3>
                <p className="font-body text-white/40 text-sm md:text-base leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>

          <div
            className={`mt-20 pt-16 border-t border-white/10 text-center transition-all duration-1000 delay-500 ${
              valuesInView ? "opacity-100" : "opacity-0"
            }`}
          >
            <p className="font-display text-2xl md:text-3xl text-white mb-6">
              Ready to start the conversation?
            </p>
            <Link
              to="/catapult/journey"
              className="inline-flex items-center gap-3 font-body text-sm tracking-[0.08em] uppercase px-8 py-4 bg-white text-stone-900 hover:bg-stone-100 transition-colors duration-300 group"
            >
              Begin Your Journey
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
